<template>
<div class="e-calc__calculator">

    <div class="e-calc__wrapper">
        <div class="e-calc__form">
            <div class="e-calc__form-wrapper">

                <h4 class="e-calc__form-title">Calculate your estimated return</h4>

                <div class="e-calc__fieldset">

                    <div class="e-calc__field">
                        <label for="amount">Amount to invest</label>
                        
                        <div class="e-calc__deposit-wrapper">
                            <input 
                                id="amount"
                                v-model="deposit" 
                                type="text" 
                                v-on:keyup="checkDepositAmount"
                                inputmode="numeric"
                                pattern="[0-9]*"
                                @input="filterNumbers" 
                            />
                            <span class="e-calc__deposit-label">$</span>
                        </div>
                    </div>

                    <div class="e-calc__field" v-if="deposit && !lowDeposit || formCompleted">
                        <label for="rateTerm">Your preferred term and rate</label>
                        <select v-model="selectedRate" id="rateTerm">
                            <option value="">Select rate</option>
                            <!-- <option v-for="rate in ratesTerms" :value="rate.value" :key="rate.id">
                                {{ rate.value }}
                            </option> -->

                            <option 
                                v-for="term in termList" 
                                :value="getRateValue(term.term) + ',' + term.term"
                                :key="term.id"
                            >
                                {{ term.label }} - {{ getRateValue(term.term) }}%
                            </option>
                        </select>
                    </div>

                    <div class="e-calc__field" v-if="selectedRate || formCompleted">
                        <label for="frequency">What would you like to do with your interest?</label>
                        <select id="frequency" v-model="selectedFrequency">
                            <option value="">Select interest</option>
                            <option v-for="option in frequencyOptions" :key="option.id" :value="option.value">
                            {{ option.label }}
                            </option>
                        </select>
                    </div>

                    <div class="e-calc__field" v-if="selectedFrequency || formCompleted">
                        <label for="instruction">What do you want to do at the end of your term?</label>
                        <select id="instruction" v-model="selectedMaturity" @change="checkFieldsCompleted">
                            <option value="">Please select</option>
                            <option v-for="option in filteredMaturityOptions" :key="option.id" :value="option.value">
                            {{ option.label }}
                            </option>
                        </select>
                    </div>
                </div>

            </div>
        </div>

        <div class="e-calc__result">

            <p class="e-calc__result-low-deposit" v-if="lowDeposit">
                Sorry, you need at least $1,000 for a term investment. How about an <a href="/everyday/savings-accounts#sbs-i-save-the-savings-account-that-helps-you-stay-on-track-452">i-save account</a> which offers you flexible savings options.
            </p>

            <div v-if="formCompleted" class="e-calc__result-wrapper">

                <h2 class="e-calc__result-title">Your estimated return</h2>
                <h3 class="e-calc__result-value">{{ calculatedReturn() }}</h3>
                <p class="e-calc__result-disclaimer">Based on our interest rate of {{ this.selectedRate.split(',')[0] }}% p.a, excluding any taxes.</p>

            </div>
        </div>

        <div class="e-calc__actions">
            
            <button v-if="formCompleted" :disabled="!formValid" @click="openSummaryPanel" class="e-calc__next button button--primary">Next</button>
            <p class="e-calc__summary-error" :class="{ 'e-calc__summary-error--show' : formCompleted && !formValid }">Please complete all fields.</p>

        </div>

    </div>

    <div class="e-calc__summary" :class="{ 'e-calc__summary--open': summaryPanelOpen }">

        <div class="e-calc__summary-wrapper">

            <div class="e-calc__summary-column-1">

                <h2 class="e-calc__summary-title">Confirm your investment details</h2>

                <button @click="submitToApplication" :disabled="!formValid" class="button button--primary">Let's do this</button>
                <button @click="clearForm" class="button button--secondary">Cancel</button>

                <!-- <p class="e-calc__summary-error" :class="{ 'e-calc__summary-error--show' : !formValid }">Please complete all fields.</p> -->

            </div>

            <div class="e-calc__summary-column-2">

                <div class="e-calc__summary-item">
                    <p>Account type</p>
                    <p>Share Term Investment</p>
                </div>

                <div class="e-calc__summary-item">
                    <p>Investment amount</p>
                    <p :class="{ 'e-calc__summary-item-error': !deposit }">{{ formatedDeposit(deposit) }}</p>
                </div>

                <div class="e-calc__summary-item">
                    <p>Term and rate</p>
                    <p :class="{ 'e-calc__summary-item-error': !selectedRate }">{{ retrieveDataLabel('rates', selectedRate) }}</p>
                </div>

                <div class="e-calc__summary-item">
                    <p>Interest frequency</p>
                    <p :class="{ 'e-calc__summary-item-error': !selectedFrequency }">{{ retrieveDataLabel('frequency', selectedFrequency) }}</p>
                </div>

                <div class="e-calc__summary-item">
                    <p>Maturity instruction</p>
                    <p :class="{ 'e-calc__summary-item-error': !selectedMaturity }">{{ retrieveDataLabel('instruction', selectedMaturity) }}</p>
                </div>

                <div class="e-calc__summary-statement" v-html="summaryContent(selectedFrequency, selectedMaturity)">
                </div>

            </div>

        </div>

    </div>

</div>
</template>

<script>
import { mapFields } from "vuex-map-fields";

export default {
    props: {
        productId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            lowDeposit: false,
            formCompleted: false,
            summaryPanelOpen: false,
            formValid: true,
        }
    },
    mounted() {
        // call state action retrieveRatesForTermInvestment
        this.$store.dispatch('retrieveRatesForTermInvestment', this.productId);
        this.$store.dispatch('retrieveQikBaseURL');        
        this.$store.dispatch('retrieveStandstoneBaseURL');
    },
    computed: {
        filteredMaturityOptions() {
            // filter maturity options based on selected frequency
            // If selectedFrequency is 'payoutqtr' or 'payout', filter out 'riprincipal'
            if (this.selectedFrequency === 'payoutqtr' || this.selectedFrequency === 'payout') {
                return this.maturityOptions.filter(option => option.value !== 'riprincipal');
            }
            return this.maturityOptions;
        },
        ...mapFields([
            "config.sandstoneBaseURL",
            "termInvestmentCalculator.deposit",
            "termInvestmentCalculator.selectedRate",
            "termInvestmentCalculator.selectedFrequency",
            "termInvestmentCalculator.selectedMaturity",
            "termInvestmentCalculator.ratesTerms",
            "termInvestmentCalculator.termList",
            "termInvestmentCalculator.frequencyOptions",
            "termInvestmentCalculator.maturityOptions",
        ])
    },
    watch: {
        deposit(newVal) {
            if (newVal == '') {
                this.formValid = false;
            } else {
                this.formValid = true;
            }
        },
        selectedRate(newVal) {
            if (newVal == '') {
                this.formValid = false;
            } else {
                this.formValid = true;
            }

            if (newVal != '') {
                this.calculatedReturn();
            }
        },
        selectedFrequency(newVal) {
            if (newVal == '') {
                this.formValid = false;
            } else {
                this.formValid = true;
            }

            if (newVal != '') {
                this.calculatedReturn();
            }

            if (newVal == 'payout' || newVal == 'payoutqtr') {
                this.selectedMaturity = '';
            }
        },
        selectedMaturity(newVal) {
            if (newVal == '') {
                this.formValid = false;
            } else {
                this.formValid = true;
            }

            if (newVal != '') {
                this.calculatedReturn();
            }
        }
    },
    methods: {
        checkDepositAmount() {
            if (this.deposit < 1000) {
                this.lowDeposit = true;
                this.formValid = false;
            } else {
                this.lowDeposit = false;
            }
        },
        filterNumbers(event) {
            // Remove non-digit characters
            const cleaned = event.target.value.replace(/\D/g, '');
            this.deposit = cleaned;
        },
        calculatedReturn() {
            
            if (isNaN(this.deposit) || this.deposit === null || this.deposit === '' ) return "$0.00"; // Handle edge cases

            if (!this.selectedRate || !this.selectedFrequency || !this.selectedMaturity) return "$0.00"; // Handle edge cases

            const investment = parseInt(this.deposit);
            const rate = parseFloat(this.selectedRate.split(',')[0]); // convert selectedRate into rate
            const interestRate = rate / 100; // Convert to decimal
            const termInMonths = parseInt(this.selectedRate.split(',')[1]); // In months            
            let compounding = 1; // Default to annual compounding

            // Set compounding frequency based on selectedFrequency
            // As per SBS provided spreadsheet
            switch (this.selectedFrequency) {
                case "payout":
                    compounding = 1;
                    break;
                case "payoutqtr":
                    compounding = 1;
                    break;
                case "compoundqtr":
                    compounding = 4;
                    break;
                case "compound":
                    compounding = 1;
                    break;
                default:
                    compounding = 1;
            }
            
            let interest;
            if (this.selectedFrequency === "compound") {
                interest = investment * interestRate * (termInMonths / 12);
            } else {
                // Base Growth Rate Per Period
                const firstPart = 1 + (interestRate / compounding);
                // console.log('1 ' + firstPart);

                // Exponent Term for Compounding Growth
                const secondPart = (termInMonths / 12) * compounding;
                // console.log('2 ' + secondPart);

                // Effective Interest Growth - Interest Earned as a Fraction of Principal
                const thirdPart = Math.pow(firstPart, secondPart) - 1;
                // console.log('3a ' + thirdPart);
                // const trimmedThirdPart = parseFloat(thirdPart.toString().slice(0, thirdPart.toString().indexOf('.') + 4));
                // console.log('3 ' + trimmedThirdPart);

                // Final Interest Calculation
                interest = investment * thirdPart;
                // console.log(interest);
            }

            // Round to two decimal places
            let estimatedReturn = Math.round(interest * 100) / 100;

            return `$${Number(estimatedReturn)
                .toFixed(2) // Ensure two decimal places
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`; // Add commas manually
        },
        checkFieldsCompleted() {
            // when last dropdown is selected, check if all fields are completed
            // set formCompleted to true so all remain in view
            if (this.deposit && this.selectedRate && this.selectedFrequency && this.selectedMaturity) {
                this.formCompleted = true;
            }
        },
        validateForm() {
            // start with invalid form
            this.formValid = false;

            // check all fields are complete
            if (this.deposit || this.selectedRate || this.selectedFrequency || this.selectedMaturity) {
                this.formValid = true;
            }
        },
        openSummaryPanel() {
            if (!this.formValid) return;

            this.summaryPanelOpen = true;

            // scroll window to summary panel
            const summaryPanel = document.querySelector('.e-calc__summary');
            summaryPanel.scrollIntoView({ behavior: 'smooth' });
        },
        getRateValue(term) {
            const rate = this.ratesTerms.find(r => r.term === term);
            return rate ? rate.value : 'N/A';
        },
        retrieveDataLabel(type, value) {            
            if (type === 'rates') {
                // reformat the "rate,term" string
                let rate = value.split(',')[0];
                let months = value.split(',')[1];
                return `${months} months - ${parseFloat(rate).toFixed(2)}%`;
            }

            if (type === 'frequency') {
                const item = this.frequencyOptions.find(term => term.value === value);
                return item ? item.summary : 'Please select interest'; // Return label if found, otherwise return null 
            }

            if (type === 'instruction') {
                const item = this.maturityOptions.find(term => term.value === value);
                return item ? item.summary : 'Please select'; // Return label if found, otherwise return null 
            }
        },
        summaryContent(selectedFrequency, selectedMaturity) {
            // return different content based on frequency and maturity

            let text = '';

            if (
                (selectedFrequency == 'payoutqtr' && selectedMaturity == 'reinvest') ||         // paid quarterly && reinvest
                (selectedFrequency == 'payoutqtr' && selectedMaturity == 'payout')              // paid quarterly && payout
            ){
                text = 'Configured for passive income;Income paid to you every quarter';
            }

            if (
                (selectedFrequency == 'compoundqtr' && selectedMaturity == 'reinvest') ||       // reinvested quarterly && reinvest
                (selectedFrequency == 'compoundqtr' && selectedMaturity == 'payout') ||         // reinvested quarterly && payout
                (selectedFrequency == 'compound' && selectedMaturity == 'reinvest') ||          // paid at maturity && reinvest
                (selectedFrequency == 'compound' && selectedMaturity == 'payout')          // paid at maturity && payout
            ){
                text = 'Configured for wealth accumulation;Returns are reinvested to grow your investment';
            }

            if (
                (selectedFrequency == 'payout' && selectedMaturity == 'reinvest') ||           // paid monthly && reinvest
                (selectedFrequency == 'payout' && selectedMaturity == 'payout')                // paid monthly && payout
            ) {
                text = 'Configured for passive income;Income paid to you every month';
            }

            if (
                (selectedFrequency == 'payoutqtr' && selectedMaturity == 'riprincipal') ||         // paid quarterly && reinvest principle
                (selectedFrequency == 'compound' && selectedMaturity == 'riprincipal') ||            // paid at maturity && reinvest principle
                (selectedFrequency == 'compoundqtr' && selectedMaturity == 'riprincipal')            // reinvested quarterly && reinvest principle
            ) {
                text = 'Configured for periodic income;Maintain your initial investment, while receiving income paid to you at the end of each term';
            }

            if (text != '') {
                // convert text into
                return '<h3 class="e-calc__summary-subheading">' + text.split(';')[0] + '</h3><p class="e-calc__summary-content">' +  text.split(';')[1] + '</p>';
            }
        },
        formatedDeposit(value) {
            // Handle edge cases
            if (isNaN(value) || value === null || value === '') return "Please enter deposit"; 

            return `$${Number(value)
                .toFixed(2) // Ensure two decimal places
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`; // Add commas manually
        },
        submitToApplication() {
            // console.log('submitting application');

            let applicationLink = this.buildApplicationLink();         
            // console.log( applicationLink );
            
            // Redirect to the application link in the current tab
            window.location.href = applicationLink;   
            
        },
        buildApplicationLink() {
            let baseURL = this.sandstoneBaseURL;

            // convert data for URL
            let term = this.selectedRate.split(',')[1];

            let maturity = this.maturityOptions.find(type => type.value === this.selectedMaturity);
            let frequency = this.frequencyOptions.find(type => type.value === this.selectedFrequency);

            // default urls
            let maturityURL = maturity ? maturity.url : '';
            let frequencyURL = frequency ? frequency.url : '';

            // Override maturityURL if required
            const isReinvestMaturity = this.selectedMaturity === 'reinvest';
            const isPayoutFrequency = ['payout', 'payoutqtr'].includes(this.selectedFrequency);
            if (isReinvestMaturity && isPayoutFrequency) {
                maturityURL = 'PRINCIPAL_ROLLOVER';
            }

            // determine account number based on selected frequency
            const accountNumberMap = {
                'payoutqtr|Quarterly': 603,
                'compoundqtr|Reinvested Quarterly': 604,
                'compound|At Maturity': 611,
                'payout|Monthly': 617
            };

            const key = `${frequency.value}|${frequency.summary}`;
            const accountNumber = accountNumberMap[key] || null;
            
            // build URL
            let params = [
                `productCode=${accountNumber}`,
                `depositTerm=${term}`,
                `depositAmount=${this.deposit}`,
                `maturityOption=${maturityURL}`,
                `interestPayment=${frequencyURL}`,
                `uniqueIdentifier=${this.generateUUID()}`
            ];

            return `${baseURL}?${params.join('&')}`;
        },
        generateUUID() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                var r = Math.random() * 16 | 0,
                    v = c == 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        },
        clearForm() {
            this.deposit = '';
            this.selectedRate = '';
            this.selectedFrequency = '';
            this.selectedMaturity = '';
            this.lowDeposit = false;
            this.formCompleted = false;
            this.formValid = true;
            this.summaryPanelOpen = false;

            // scroll back to element top
            const calculator = document.querySelector('.e-calc');
            calculator.scrollIntoView({ behavior: 'smooth' });

        },
    }
}
</script>